import { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtendedFeature, GeoGeometryObjects } from 'd3-geo';
import { GeoJsonProperties } from 'geojson';
import { feature } from 'topojson-client';
import { GeometryObject, Topology } from 'topojson-specification';
import { useNavigationData } from '../../modules/NavigationProvider';
import { usePolesContext } from '../../modules/poles/PolesContext';
import { Pole } from '../../modules/poles/_models';
import MapChart from '../../components/MapChart';
import geoJson from '../../assets/world-countries.json';
import List from './components/List';
import Item from './components/Item';

const MapPage: FC = () => {
  const { t } = useTranslation('map');
  const { setPath, setButtonText, setHeaderText } = useNavigationData();
  const { poles, setPoleId } = usePolesContext();
  const [selectedPark, setSelectedPark] = useState<number | null>(null);
  const [selectedGeo, setSelectedGeo] = useState<ExtendedFeature<
    GeoGeometryObjects | null,
    GeoJsonProperties
  > | null>(null);

  const listCountries = useMemo(() => {
    return geoJson.objects.countries.geometries.filter((feature) =>
      poles?.find((pole) => pole.country === feature.properties.name)
    );
  }, [poles]);

  useLayoutEffect(() => {
    setPath(
      (selectedGeo && listCountries.length > 1) ||
        (selectedPark && poles && poles.length > 1)
        ? '/'
        : undefined
    );
    setButtonText();
    setHeaderText(
      selectedPark
        ? poles.find((pole) => pole.id === selectedPark)?.name_park ||
            t('park', { id: selectedPark })
        : t('select.park') || t('select.country')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listCountries, poles, selectedGeo, selectedPark]);

  useEffect(() => {
    if (listCountries.length === 1) {
      handleItemClick(listCountries[0]);
    }
  }, [listCountries]);

  const handleItemClick = (country: Record<string, unknown>) => {
    const geojsonFeature = feature(
      geoJson as unknown as Topology,
      country as unknown as GeometryObject
    ) as ExtendedFeature<GeoGeometryObjects | null, GeoJsonProperties>;

    setSelectedGeo(geojsonFeature);
  };

  const parkMarkers = useMemo(() => {
    if (!selectedGeo || selectedPark) return [];

    return poles.reduce((acc, pole: Pole) => {
      if (!isNaN(pole.lat) && !isNaN(pole.lng)) {
        const exists = acc.find((m) => m.id === pole.id_park);
        if (!exists) {
          acc.push({
            id: pole.id_park,
            name: pole.name_park ?? t('park', { id: pole.id_park }),
            coordinates: [pole.lng, pole.lat],
          });
        }
      }
      return acc;
    }, [] as { id: number; name: string; coordinates: [number, number] }[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poles, selectedGeo, selectedPark]);

  useEffect(() => {
    if (parkMarkers.length === 1) {
      setSelectedPark(parkMarkers[0].id);
    }
  }, [parkMarkers]);

  return (
    <div className="map-container" style={{ width: '100%', height: '100%' }}>
      <MapChart
        markers={parkMarkers.map((m) => ({
          ...m,
          onClick: () => setSelectedPark(m.id),
        }))}
        selectedGeo={selectedGeo}
      />
      <List show={!selectedGeo}>
        {listCountries.map((country, index) => (
          <Item key={index} onClick={() => handleItemClick(country)}>
            <span>{country.properties.name}</span>
          </Item>
        ))}
      </List>
      <List show={selectedPark !== null && !isNaN(selectedPark)}>
        {poles
          ?.filter((pole) => pole.id_park === selectedPark)
          .map((pole, index) => (
            <Item key={index} onClick={() => setPoleId(pole.id)}>
              <span>{pole.name ?? `VihuPole ${pole.id}`}</span>
            </Item>
          ))}
      </List>
    </div>
  );
};

export default MapPage;
